import { useEffect, useState } from 'react';
import { currentPolicies } from '../utils/policyStatus';

const STORAGE_PREFIX = 'pgi.glovebox.policies.';

const storageKey = (userId) => `${STORAGE_PREFIX}${userId}`;

const readCache = (userId) => {
  if (!userId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.policies)) return null;

    // Filter on READ as well as on write. Devices that ran an earlier build
    // already have expired policies sitting in localStorage, and an expired
    // ID card must not survive in the offline copy.
    const current = currentPolicies(parsed.policies);

    // Rewrite the entry when anything was dropped, so the expired policy data
    // actually leaves the device instead of lingering behind the read filter.
    // Without this the guarantee is only "cannot be shown", not "is not there".
    if (current.length !== parsed.policies.length) {
      if (current.length === 0) {
        window.localStorage.removeItem(storageKey(userId));
      } else {
        window.localStorage.setItem(
          storageKey(userId),
          JSON.stringify({ ...parsed, policies: current })
        );
      }
    }

    if (current.length === 0) return null;
    return { ...parsed, policies: current };
  } catch {
    return null;
  }
};

const writeCache = (userId, policies) => {
  if (!userId) return;
  try {
    window.localStorage.setItem(
      storageKey(userId),
      JSON.stringify({ policies: currentPolicies(policies), cachedAt: new Date().toISOString() })
    );
  } catch {
    // Quota or private-browsing failures are non-fatal — the app still works
    // online, it just won't have an offline copy.
  }
};

/**
 * Keeps a local copy of the client's policies so proof of insurance is
 * available with no signal — the whole point of a glovebox app.
 *
 * Returns the live policies when they're loaded, and the last-known-good
 * cached copy otherwise, along with when that copy was taken.
 */
export const useOfflinePolicies = (user) => {
  const userId = user?.id;
  const livePolicies = user?.policies;

  const [cached, setCached] = useState(() => readCache(userId));

  // A different account on this device must not inherit the previous
  // client's saved policies. Runs before the snapshot below writes anything.
  useEffect(() => {
    if (!userId) return;
    clearOtherUsersOfflinePolicies(userId);
  }, [userId]);

  // Refresh the local snapshot whenever a live list arrives.
  useEffect(() => {
    if (!userId) return;
    const live = currentPolicies(livePolicies);
    if (live.length > 0) {
      writeCache(userId, live);
      setCached({ policies: live, cachedAt: new Date().toISOString() });
    } else {
      setCached(readCache(userId));
    }
  }, [userId, livePolicies]);

  // Only current coverage reaches this surface. An expired card is not proof
  // of anything at the roadside, which is the only reason this hook exists.
  const live = currentPolicies(livePolicies);
  const hasLive = live.length > 0;
  const policies = hasLive ? live : cached?.policies || [];

  return {
    policies,
    // True when the client is looking at a stored copy rather than fresh data.
    isFromCache: !hasLive && policies.length > 0,
    cachedAt: cached?.cachedAt || null,
  };
};

/**
 * Removes every cached policy set that does NOT belong to `keepUserId`.
 *
 * The shared-phone case: a client signs in on a handset that still holds a
 * previous client's saved policies (their session expired rather than being
 * signed out, so F5 rule 3 deliberately kept the entry). Nobody should
 * inherit someone else's coverage, so the new sign-in clears the rest.
 */
export const clearOtherUsersOfflinePolicies = (keepUserId) => {
  try {
    const keep = keepUserId ? storageKey(keepUserId) : null;
    const doomed = [];
    // Collect first: removing while iterating shifts the index.
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX) && key !== keep) doomed.push(key);
    }
    doomed.forEach((key) => window.localStorage.removeItem(key));
    return doomed.length;
  } catch {
    // Private browsing or a locked-down storage API — best-effort only.
    return 0;
  }
};

export const clearOfflinePolicies = (userId) => {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // Nothing to do — clearing is best-effort.
  }
};
